/**
 * Executive Summary card with copy-to-clipboard functionality
 * Displays stakeholder-ready summary text that can be copied for emails/Slack
 */

import { useState, useCallback } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';

export interface ExecutiveSummaryCardProps {
  summary: string;
  className?: string;
}

export function ExecutiveSummaryCard({ summary, className }: ExecutiveSummaryCardProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  /**
   * Copy summary text to clipboard
   */
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopyState('copied');

      // Reset after 2 seconds
      setTimeout(() => {
        setCopyState('idle');
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      setCopyState('error');

      // Reset after 2 seconds
      setTimeout(() => {
        setCopyState('idle');
      }, 2000);
    }
  }, [summary]);

  /**
   * Get button text based on copy state
   */
  const getButtonText = () => {
    switch (copyState) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Copy failed';
      default:
        return 'Copy Summary';
    }
  };

  /**
   * Get button icon based on copy state
   */
  const getButtonIcon = () => {
    if (copyState === 'copied') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      );
    }

    if (copyState === 'error') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      );
    }

    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    );
  };

  return (
    <Card className={cn('', className)}>
      {/* Header with Copy Button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Executive Summary</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Ready to share with stakeholders
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          className={cn(
            'flex items-center gap-2 transition-colors',
            copyState === 'copied' && 'bg-green-100 text-green-700 hover:bg-green-100',
            copyState === 'error' && 'bg-red-100 text-red-700 hover:bg-red-100'
          )}
        >
          {getButtonIcon()}
          <span>{getButtonText()}</span>
        </Button>
      </div>

      {/* Summary Content */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
          {summary}
        </p>
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>Click &quot;Copy Summary&quot; to paste into emails, Slack, or documents</span>
      </p>
    </Card>
  );
}
